import { View, SideNavView, ContextMenu, TableView, Dialog } from 'web-mojo';
import { VendingProduct } from '@portal/models/VendingProduct.js';
import { VendingTransactionList } from '@portal/models/VendingTransaction.js';

class ProductView extends View {
    constructor(options = {}) {
        super({
            className: 'product-view',
            ...options
        });
        this.model = options.model || new VendingProduct(options.data || {});
        this.template = `
            <div class="product-view-container">
                <div data-container="header"></div>
                <div data-container="sidenav" style="min-height: 400px;"></div>
            </div>
        `;
    }

    async onInit() {
        // Header with product name, price, stock status, context menu
        this.header = new View({
            containerId: 'header',
            template: `
            <div class="d-flex justify-content-between align-items-start mb-4">
                <div class="d-flex align-items-center gap-3">
                    {{#model.image.thumbnail}}
                    <img src="{{model.image.thumbnail}}" class="rounded border" style="width: 56px; height: 56px; object-fit: cover; cursor: pointer;" data-action="update-image">
                    {{/model.image.thumbnail}}
                    {{^model.image.thumbnail}}
                    <div class="d-flex align-items-center justify-content-center rounded bg-light" style="width: 56px; height: 56px; cursor: pointer;" data-action="update-image">
                        <i class="bi bi-box-seam text-secondary" style="font-size: 1.5rem;"></i>
                    </div>
                    {{/model.image.thumbnail}}
                    <div>
                        <h3 class="mb-0">{{model.name|default('Product')}}</h3>
                        <div class="d-flex align-items-center gap-2 mt-1">
                            <span class="badge bg-primary bg-opacity-10 text-primary">{{model.sku|default('No SKU')}}</span>
                            {{#model.is_active|bool}}
                                <span class="badge bg-success bg-opacity-10 text-success">Active</span>
                            {{/model.is_active|bool}}
                            {{^model.is_active|bool}}
                                <span class="badge bg-secondary bg-opacity-10 text-secondary">Inactive</span>
                            {{/model.is_active|bool}}
                        </div>
                    </div>
                </div>
                <div class="d-flex align-items-start gap-3">
                    <div class="text-end">
                        <div class="fs-4 fw-bold">{{model.price_formatted}}</div>
                        <div class="text-muted small">{{model.currency|default('USD')}}</div>
                    </div>
                    <div data-container="context-menu"></div>
                </div>
            </div>`
        });
        this.header.setModel(this.model);
        this.addChild(this.header);

        // Details section
        const detailsView = new View({
            model: this.model,
            template: `
                <style>
                    .pv-field-row { display: flex; align-items: baseline; padding: 0.5rem 0; border-bottom: 1px solid #f0f0f0; }
                    .pv-field-row:last-child { border-bottom: none; }
                    .pv-field-label { width: 140px; font-size: 0.78rem; color: #6c757d; flex-shrink: 0; }
                    .pv-field-value { flex: 1; font-size: 0.88rem; color: #212529; }
                    .pv-section-label { font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #adb5bd; margin-bottom: 0.5rem; margin-top: 1.5rem; }
                    .pv-section-label:first-child { margin-top: 0; }
                </style>
                <div class="pv-section-label">Product</div>
                <div class="pv-field-row">
                    <div class="pv-field-label">Name</div>
                    <div class="pv-field-value">{{model.name|default('—')}}</div>
                </div>
                <div class="pv-field-row">
                    <div class="pv-field-label">SKU</div>
                    <div class="pv-field-value" style="font-family: ui-monospace, monospace;">{{model.sku|default('—')}}</div>
                </div>
                <div class="pv-field-row">
                    <div class="pv-field-label">Price</div>
                    <div class="pv-field-value">{{model.price_formatted|default('—')}} {{model.currency|default('USD')}}</div>
                </div>
                <div class="pv-field-row">
                    <div class="pv-field-label">Image</div>
                    <div class="pv-field-value">
                        {{#model.image.url}}<img src="{{model.image.thumbnail}}" class="rounded border" style="width: 40px; height: 40px; object-fit: cover;">{{/model.image.url}}
                        {{^model.image.url}}<span class="text-muted">No image</span>{{/model.image.url}}
                    </div>
                </div>
                <div class="pv-field-row">
                    <div class="pv-field-label">Status</div>
                    <div class="pv-field-value">
                        {{#model.is_active|bool}}<span class="badge bg-success bg-opacity-10 text-success">Active</span>{{/model.is_active|bool}}
                        {{^model.is_active|bool}}<span class="badge bg-secondary bg-opacity-10 text-secondary">Inactive</span>{{/model.is_active|bool}}
                    </div>
                </div>

                <div class="pv-section-label">Inventory</div>
                <div class="pv-field-row">
                    <div class="pv-field-label">Stock</div>
                    <div class="pv-field-value">{{model.stock_count|default('—')}}</div>
                </div>
                <div class="pv-field-row">
                    <div class="pv-field-label">Low Stock Alert</div>
                    <div class="pv-field-value">{{model.low_stock_threshold|default('—')}}</div>
                </div>

                <div class="pv-section-label">Dates</div>
                <div class="pv-field-row">
                    <div class="pv-field-label">Created</div>
                    <div class="pv-field-value">{{model.created|datetime|default('—')}}</div>
                </div>
                <div class="pv-field-row">
                    <div class="pv-field-label">Modified</div>
                    <div class="pv-field-value">{{model.modified|datetime|default('—')}}</div>
                </div>
            `
        });

        // Transactions section
        const transactionsView = new TableView({
            collection: new VendingTransactionList({
                params: { product: this.model.get('id'), size: 10, sort: '-created' }
            }),
            hideActivePillNames: ['product'],
            clickAction: 'view',
            columns: [
                { key: 'created', label: 'Date', formatter: 'datetime', sortable: true },
                { key: 'amount_formatted', label: 'Amount' },
                { key: 'machine.name', label: 'Terminal', formatter: "default('—')" },
                { key: 'status|badge', label: 'Status' },
            ]
        });

        // SideNavView
        this.sideNavView = new SideNavView({
            containerId: 'sidenav',
            activeSection: 'details',
            navWidth: 180,
            contentPadding: '1.25rem 2rem',
            enableResponsive: true,
            minWidth: 500,
            sections: [
                { key: 'details', label: 'Details', icon: 'bi-info-circle', view: detailsView },
                { key: 'transactions', label: 'Transactions', icon: 'bi-credit-card', view: transactionsView },
            ]
        });
        this.addChild(this.sideNavView);

        // Context Menu
        const contextMenu = new ContextMenu({
            containerId: 'context-menu',
            className: 'context-menu-view header-menu-absolute',
            context: this.model,
            config: {
                icon: 'bi-three-dots-vertical',
                items: [
                    { label: 'Edit', action: 'edit', icon: 'bi-pencil' },
                    { label: 'Update Image', action: 'update-image', icon: 'bi-image' },
                    { label: 'Update Stock', action: 'update-stock', icon: 'bi-box-seam' },
                    { type: 'divider' },
                    this.model.get('is_active')
                        ? { label: 'Deactivate', action: 'deactivate', icon: 'bi-x-circle' }
                        : { label: 'Activate', action: 'activate', icon: 'bi-check-circle' },
                ]
            }
        });
        this.addChild(contextMenu);
    }

    async onActionUpdateImage() {
        const resp = await Dialog.updateModelImage({
            model: this.model,
            field: 'image',
            title: 'Product Image',
            upload: true,
        }, {
            name: 'image',
            size: 'lg',
            placeholder: 'Upload product image',
        });
        if (resp && resp.status === 200) {
            await this.render();
        }
    }

    async onActionEdit() {
        const resp = await Dialog.showModelForm({
            title: `Edit Product — ${this.model.get('name')}`,
            model: this.model,
            fields: VendingProduct.EDIT_FORM?.fields || [
                { label: 'Name', name: 'name', columns: 6 },
                { label: 'SKU', name: 'sku', columns: 6 },
                { label: 'Price (cents)', name: 'price', type: 'number', columns: 6 },
                { label: 'Currency', name: 'currency', columns: 6 },
            ],
        });
        if (resp) await this.render();
    }

    async onActionUpdateStock() {
        const data = await Dialog.showForm({
            title: `Update Stock — ${this.model.get('name')}`,
            size: 'sm',
            fields: [
                { type: 'number', name: 'stock_count', label: 'Stock Count', required: true, columns: 12 },
            ]
        });
        if (!data) return;
        const resp = await this.model.save({ stock_count: data.stock_count });
        if (resp?.success) {
            this.getApp().toast.success('Stock updated');
            await this.render();
        } else {
            this.getApp().toast.error('Failed to update stock');
        }
    }

    async onActionDeactivate() {
        const confirmed = await Dialog.confirm({
            title: 'Deactivate Product',
            message: `Deactivate "${this.model.get('name')}"?`,
            confirmText: 'Deactivate',
            confirmClass: 'btn-danger',
        });
        if (!confirmed) return;
        const resp = await this.model.save({ is_active: false });
        if (resp?.success) {
            this.getApp().toast.success('Product deactivated');
            await this.render();
        } else {
            this.getApp().toast.error('Failed to deactivate');
        }
    }

    async onActionActivate() {
        const resp = await this.model.save({ is_active: true });
        if (resp?.success) {
            this.getApp().toast.success('Product activated');
            await this.render();
        } else {
            this.getApp().toast.error('Failed to activate');
        }
    }

    _onModelChange() {}
}

VendingProduct.VIEW_CLASS = ProductView;
export default ProductView;
